import type { XYEffect } from "../engine/effects/xyEffect";
import type { AnimationLoader } from "./animationLoader";
import { ManagedAnimation } from "./sprites/managedAnimation";
import type { UnboundedEffectRenderer } from "./unboundedEffectRenderer";

interface PendingEffect {
	effect: XYEffect;
	anim: ManagedAnimation;
}

/**
 * Holds preloaded unbounded sprite effects pending release. Mirrors SoundQueue:
 * releaseUpTo / releaseAll hand effects to UnboundedEffectRenderer as the EffectManager's
 * order clock reaches each one, so an effect STARTS in order (e.g. the sword's slash
 * cascade) without — being unbounded — blocking that clock.
 */
export class UnboundedEffectQueue {
	private pending: PendingEffect[] = [];

	async load(effects: XYEffect[], animationLoader: AnimationLoader): Promise<void> {
		for (const effect of effects) {
			const animation = await animationLoader.getAnimationForEffect(effect);
			if (animation === null) continue;
			this.pending.push({ effect, anim: new ManagedAnimation(animation) });
		}
		this.pending.sort((a, b) => a.effect.getAnimationOrder() - b.effect.getAnimationOrder());
	}

	releaseUpTo(currentOrder: number | null, renderer: UnboundedEffectRenderer): void {
		if (currentOrder === null) return;
		while (this.pending.length > 0 && this.pending[0].effect.getAnimationOrder() <= currentOrder) {
			const { effect, anim } = this.pending.shift()!;
			renderer.play(effect, anim);
		}
	}

	releaseAll(renderer: UnboundedEffectRenderer): void {
		while (this.pending.length > 0) {
			const { effect, anim } = this.pending.shift()!;
			renderer.play(effect, anim);
		}
	}

	clear(): void {
		this.pending.length = 0;
	}
}
